import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Grid } from '@material-ui/core';
import moment from 'moment';
import { DateRange } from '@material-ui/pickers';
import { useLocation } from 'react-router-dom';
import { useApiError, useAppData, useLoading } from '../../../providers';
import { trackAmplitudeData } from '../../../services/amplitudeService';
import { useCommonStyles } from '../../../styles';
import { Header, PageTitle, ReportTitle } from '../../../components';
import { getLinkStatisticsReport } from './LinkStatisticsService';
import { Link } from '../../RegistrationLinks/RegistrationLinksModel';
import { QuickReport, QuickReportWithPercentage, ReportTable, ReportToolbarWithFilters } from './components';
import { LinkStatisticsReport, LinkStatisticsList } from './LinkStatisticsModel';
import { useStyles } from './styles';
import { ColumnConfig } from '../DataTable';

export const IBLinkStatisticsReport: FC = () => {
  const classes = useStyles();
  const commonClasses = useCommonStyles();
  const location = useLocation();
  const { setIsLoading } = useLoading();
  const { addError } = useApiError();
  const { translations } = useAppData();
  const [selectedPartnership, setSelectedPartnership] = useState<string>();
  const [selectedPeriodType, setSelectedPeriodType] = useState<number | undefined>(2);
  const [selectedLink, setSelectedLink] = useState<Link>();
  const [selectedDateRange, setSelectedDateRange] = React.useState<DateRange<Date>>([moment().subtract(1, 'months').toDate(), moment().subtract(1, 'days').toDate()]);
  const [showReport, setShowReport] = useState<boolean>(false);
  const [reportData, setReportData] = useState<LinkStatisticsReport>();
  const [translation, setTranslation] = useState<any>([]);
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);

  useEffect(() => {
    setTranslation(JSON.parse(translations));
  }, [translations]);

  useEffect(() => {
    if (location.state) {
      const state = location.state as any;
      const preselected = state.data;
      setSelectedLink(preselected);
      if (preselected.partnershipClientShortLinkDetails) {
        setSelectedPartnership(preselected.partnershipClientShortLinkDetails.partnershipCode);
      } else {
        setSelectedPartnership(preselected.partnershipLinkDetails.partnershipCode);
      }
    }
  }, [location]);

  const columns = useMemo(
    (): ColumnConfig<LinkStatisticsList>[] => [
      { key: 'shortCode', label: translation.short_code || '', type: 'string' },
      { key: 'partnershipCode', label: translation.text_6275 || '', type: 'string' },
      {
        key: 'startDate',
        label: translation.text_841 || '',
        type: 'date',
        format: (v, row) => (v || row.endDate ? `${moment(row.startDate).format('DD-MM-YYYY')} - ${moment(row.endDate).format('DD-MM-YYYY')}` : '-'),
      },
      { key: 'registrationsCount', label: translation.registrations || '', type: 'number' },
      { key: 'fullKycCount', label: translation.full_kyc || '', type: 'number' },
      { key: 'firstTimeFundsInCount', label: translation.first_time_deposits || '', type: 'number' },
      { key: 'startedTradingCount', label: translation.clients_started_trading || '', type: 'number' },
      { key: 'tradingAccountsActiveCount', label: translation.active_trading_accounts || '', type: 'number' },
      { key: 'tradingAccountsInactiveCount', label: translation.inactive_trading_accounts || '', type: 'number' },
    ],
    [translation],
  );

  useEffect(() => {
    setSelectedColumns(columns.map((col) => col.label));
  }, [columns]);

  const changeSelectedPartnership = useCallback(async (option: string | undefined) => {
    setSelectedPartnership(option);
    setShowReport(false);
    setReportData(undefined);
  }, []);

  const getReportData = useCallback(
    async (partnership: string | undefined, dateRange: DateRange<Date>, periodType: number, linkCode: string | undefined) => {
      setIsLoading(true);
      try {
        trackAmplitudeData('partnerPortal_action_report', { type: 'Requested', report_name: 'IB Link Statistics Report' });
        const response = await getLinkStatisticsReport(moment(dateRange[0]).format('YYYY-MM-DD'), moment(dateRange[1]).format('YYYY-MM-DD'), periodType, partnership, linkCode);
        setReportData(response.data);
        setShowReport(true);
      } catch (error: any) {
        addError(JSON.stringify(error.response));
      } finally {
        setIsLoading(false);
      }
    },
    [addError, setIsLoading],
  );

  const handleNewReportRequest = useCallback(async () => {
    if (selectedPeriodType) {
      await getReportData(selectedPartnership, selectedDateRange, selectedPeriodType, selectedLink?.shortCode);
    }
  }, [selectedPartnership, selectedDateRange, selectedPeriodType, selectedLink, getReportData]);

  const handleFiltersApplied = useCallback(
    async (partnership: string | undefined, period: number | undefined, link: Link | undefined) => {
      setSelectedPartnership(partnership);
      setSelectedPeriodType(period);
      setSelectedLink(link);
      if (period) {
        await getReportData(partnership, selectedDateRange, period, link?.shortCode);
      }
    },
    [selectedDateRange, getReportData],
  );

  return (
    <>
      <Header title={translation.text_6663} />
      <Grid container direction="column" className={classes.root}>
        <PageTitle title={translation.text_6663} />
        <Grid container direction="column" className={commonClasses.mainContainer}>
          <ReportTitle
            translation={translation}
            title={translation.ibLinkStatisticsReport}
            exportFileName={translation.ibLinkStatisticsReport}
            columns={columns}
            allData={reportData?.linkStatisticsList || []}
            selectedColumns={selectedColumns}
            enableButtons={showReport}
            reportNameInEn="IB Link statistics Report"
          />
          <Grid container direction="column">
            <ReportToolbarWithFilters
              link={selectedLink}
              partnershipCode={selectedPartnership}
              translation={translation}
              dateRange={selectedDateRange}
              handleDateRangeChange={(dateRange) => {
                setSelectedDateRange(dateRange);
                setShowReport(false);
              }}
              onPeriodChange={(periodType) => setSelectedPeriodType(periodType)}
              onLinkChange={(link) => setSelectedLink(link)}
              onPartnershipChange={changeSelectedPartnership}
              handleButtonClick={handleNewReportRequest}
              handleFilterApplied={handleFiltersApplied}
            />
            {selectedDateRange[0] && (
              <Grid item xs={12}>
                <Grid container direction="row" spacing={2} className={classes.panels}>
                  <Grid item className={classes.quickReports}>
                    <QuickReportWithPercentage
                      translation={translation}
                      title={translation.registrations}
                      value={reportData?.linkStatisticsTotals.registrationsCount || 0}
                      percentage={reportData?.linkStatisticsTotals.registrationsPercent || 0}
                    />
                  </Grid>
                  <Grid item className={classes.quickReports}>
                    <QuickReportWithPercentage
                      translation={translation}
                      title={translation.kyc_verified}
                      tooltip={translation.kyc_verified_tooltip}
                      value={reportData?.linkStatisticsTotals.fullKycCount || 0}
                      percentage={reportData?.linkStatisticsTotals.fullKycPercent || 0}
                    />
                  </Grid>
                  <Grid item className={classes.quickReports}>
                    <QuickReportWithPercentage
                      translation={translation}
                      title={translation.first_time_deposits}
                      value={reportData?.linkStatisticsTotals.firstTimeFundsInCount || 0}
                      percentage={reportData?.linkStatisticsTotals.firstTimeFundsInPercent || 0}
                    />
                  </Grid>
                  <Grid item className={classes.quickReports}>
                    <QuickReportWithPercentage
                      translation={translation}
                      title={translation.clients_started_trading}
                      value={reportData?.linkStatisticsTotals.startedTradingCount || 0}
                      percentage={reportData?.linkStatisticsTotals.startedTradingPercent || 0}
                    />
                  </Grid>
                  <Grid item className={classes.quickReports}>
                    <QuickReport title={translation.active_trading_accounts} tooltip={translation.active_trading_accounts_tooltip} value={reportData?.linkStatisticsTotals.tradingAccountsActiveCount || 0} />
                  </Grid>
                  <Grid item className={classes.quickReports}>
                    <QuickReport title={translation.inactive_trading_accounts} tooltip={translation.inactive_trading_accounts_tooltip} value={reportData?.linkStatisticsTotals.tradingAccountsInactiveCount || 0} />
                  </Grid>
                </Grid>
                <ReportTable columns={columns} reportData={reportData} translation={translation} selectedColumns={selectedColumns} onSelectColumns={setSelectedColumns} />
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};
